// src/pages/lab/processing/processingtabs/components/stepExecution/GenotyperRunPanel.tsx
//
// The expanded body under a gsa_raw result row: the run's lifecycle, its per-sample statuses,
// the results-zip download and a re-run. Polls labiq every 30s until the run is terminal.

import React, { useState } from "react";
import { Alert, Button, Popconfirm, Tooltip, Typography } from "antd";
import { DownloadOutlined, ReloadOutlined, SyncOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import axios from "axios";

import { DataTable, EmptyState, InlineLoading, StatusTag } from "../../../../../../components/shared";

import { analyzeResult } from "../../../../../../api/labiq/SampleResultApi";
import { saveGenotyperArtifact } from "../../../../../../api/labiq/GenotyperApi";

import type {
	GenotyperCnvStatus,
	GenotyperMappingStatus,
	GenotyperPhaseStatus,
	GenotyperRun,
	GenotyperRunSample,
	GenotyperRunStatus,
} from "../../../../../../types/lab/GenotyperTypes";

import { useNotifications } from "../../../../../../context/NotificationContext";
import { useGenotyperRun } from "../../../../../../hooks/genetic/useGenotyperRun";

import {
	describeWarning,
	errorCodeHint,
	formatCallRate,
	GENOTYPER_RUN_STATUS_LABELS,
	isLowCallRate,
	isTerminalRunStatus,
	NO_SIGNAL_TOOLTIP,
	sentrixLabel,
	warningHint,
} from "../../../../../../utils/genotyper";
import { formatInZone } from "../../../../../../utils/time";

const { Text } = Typography;

interface GenotyperRunPanelProps {
	resultId: number;
	// Lets the results list refresh after a re-run flips the result back to pending.
	onRerun?: () => void;
}

const RUN_STATUS_COLOR: Record<GenotyperRunStatus, string> = {
	queued: "var(--mg-info)",
	extracting: "var(--mg-primary)",
	snp_import: "var(--mg-primary)",
	analysis: "var(--mg-primary)",
	loading: "var(--mg-primary)",
	completed: "var(--mg-success)",
	failed: "var(--mg-danger)",
	partial: "var(--mg-warning)",
};

const PHASE_COLOR: Record<GenotyperPhaseStatus | GenotyperCnvStatus, string> = {
	pending: "default",
	running: "var(--mg-primary)",
	completed: "var(--mg-success)",
	failed: "var(--mg-danger)",
	skipped: "default",
	no_signal: "var(--mg-warning)",
};

const MAPPING_TAG: Record<GenotyperMappingStatus, { color: string; label: string }> = {
	mapped: { color: "var(--mg-success)", label: "Mapped" },
	missing_from_sheet: { color: "var(--mg-warning)", label: "Not in sheet" },
	idat_missing: { color: "var(--mg-danger)", label: "IDAT missing" },
};

const humanise = (status: string): string => status.replace(/_/g, " ");

const formatInstant = (iso: string | null): string => (iso ? formatInZone(iso, "D MMM YYYY HH:mm") : "") || "—";

const PhaseTag: React.FC<{ name: string; status: GenotyperPhaseStatus | GenotyperCnvStatus | null }> = ({ name, status }) => {
	const resolved = status ?? "pending";
	return <StatusTag status={resolved} label={`${name} · ${humanise(resolved)}`} color={PHASE_COLOR[resolved] ?? "default"} size="small" />;
};

const PipelineTags: React.FC<{ sample: GenotyperRunSample }> = ({ sample }) => (
	<span style={{ display: "inline-flex", gap: 2, flexWrap: "wrap" }}>
		<PhaseTag name="SNP" status={sample.snp_status} />
		{sample.cnv_status === "no_signal" ? (
			<Tooltip title={NO_SIGNAL_TOOLTIP}>
				<span data-testid={`results-genotyper-cnv-no-signal-${sample.sample_id}`} style={{ cursor: "help" }}>
					<PhaseTag name="CNV" status="no_signal" />
				</span>
			</Tooltip>
		) : (
			<PhaseTag name="CNV" status={sample.cnv_status} />
		)}
		<PhaseTag name="PGx" status={sample.pgx_status} />
		<PhaseTag name="mtDNA" status={sample.mtdna_status} />
	</span>
);

const sampleColumns = (showErrors: boolean): ColumnsType<GenotyperRunSample> => [
	{
		title: "Sample",
		dataIndex: "sample_id",
		key: "sample_id",
		width: 110,
		render: (id: string) => <Text style={{ fontSize: 12, fontWeight: 500 }}>{id}</Text>,
	},
	{
		title: "Sentrix",
		key: "sentrix",
		width: 170,
		render: (_: unknown, sample: GenotyperRunSample) => (
			<Text style={{ fontSize: 11, fontFamily: "monospace" }}>{sentrixLabel(sample.sentrix_barcode, sample.sentrix_position)}</Text>
		),
	},
	{
		title: "Sex",
		dataIndex: "sex",
		key: "sex",
		width: 50,
		render: (sex: string | null) => <Text style={{ fontSize: 12 }}>{sex || "—"}</Text>,
	},
	{
		title: "Call rate",
		key: "call_rate",
		width: 90,
		render: (_: unknown, sample: GenotyperRunSample) => (
			<Text
				title={sample.calls !== null ? `${sample.calls} calls · ${sample.no_calls ?? 0} no-calls` : undefined}
				style={{
					fontSize: 12,
					fontVariantNumeric: "tabular-nums",
					color: isLowCallRate(sample.call_rate) ? "var(--mg-warning)" : undefined,
					fontWeight: isLowCallRate(sample.call_rate) ? 600 : undefined,
				}}
			>
				{formatCallRate(sample.call_rate)}
			</Text>
		),
	},
	{
		title: "Mapping",
		dataIndex: "mapping_status",
		key: "mapping_status",
		width: 110,
		render: (status: GenotyperMappingStatus) => {
			const tag = MAPPING_TAG[status] ?? { color: "default", label: humanise(status) };
			return <StatusTag status={status} label={tag.label} color={tag.color} size="small" />;
		},
	},
	{
		title: "Pipelines",
		key: "pipelines",
		render: (_: unknown, sample: GenotyperRunSample) => <PipelineTags sample={sample} />,
	},
	...(showErrors
		? [
				{
					title: "Error",
					dataIndex: "error_code",
					key: "error_code",
					width: 200,
					render: (code: string | null) =>
						code ? (
							<Text type="danger" style={{ fontSize: 11 }} title={code}>
								{errorCodeHint(code)}
							</Text>
						) : (
							<Text type="secondary" style={{ fontSize: 11 }}>
								—
							</Text>
						),
				},
		  ]
		: []),
];

const RunHeader: React.FC<{ run: GenotyperRun; isPolling: boolean }> = ({ run, isPolling }) => (
	<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
		<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
			<span data-testid="results-genotyper-status">
				<StatusTag status={run.status} label={GENOTYPER_RUN_STATUS_LABELS[run.status] ?? humanise(run.status)} color={RUN_STATUS_COLOR[run.status]} />
			</span>
			{isPolling && (
				<Text type="secondary" style={{ fontSize: 11 }}>
					<SyncOutlined spin /> refreshing every 30s
				</Text>
			)}
			<Text type="secondary" style={{ fontSize: 11 }}>
				Run #{run.id} · {run.sample_count} sample{run.sample_count === 1 ? "" : "s"}
			</Text>
		</div>
		<div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 11 }}>
			<Text style={{ fontSize: 11 }}>
				<Text type="secondary" style={{ fontSize: 11 }}>Barcodes </Text>
				{run.sentrix_barcodes?.length ? run.sentrix_barcodes.join(", ") : "—"}
			</Text>
			<Text style={{ fontSize: 11 }}>
				<Text type="secondary" style={{ fontSize: 11 }}>Manifest </Text>
				{run.manifest || "—"}
			</Text>
			<Text style={{ fontSize: 11 }}>
				<Text type="secondary" style={{ fontSize: 11 }}>Started </Text>
				{formatInstant(run.started_at)}
			</Text>
			<Text style={{ fontSize: 11 }}>
				<Text type="secondary" style={{ fontSize: 11 }}>Finished </Text>
				{formatInstant(run.finished_at)}
			</Text>
		</div>
		<div style={{ display: "inline-flex", gap: 4 }}>
			<PhaseTag name="SNP import" status={run.snp_status} />
			<PhaseTag name="Analysis" status={run.analysis_status} />
		</div>
	</div>
);

const GenotyperRunPanel: React.FC<GenotyperRunPanelProps> = ({ resultId, onRerun }) => {
	const { run, isLoading, isError, error, isPolling, refetch } = useGenotyperRun(resultId);
	const { showSuccess, showError } = useNotifications();
	const [downloading, setDownloading] = useState(false);
	const [rerunning, setRerunning] = useState(false);

	const handleDownload = async () => {
		if (!run) return;
		setDownloading(true);
		try {
			await saveGenotyperArtifact(run.id);
		} catch (err) {
			showError("The results zip could not be downloaded");
		} finally {
			setDownloading(false);
		}
	};

	const handleRerun = async () => {
		setRerunning(true);
		try {
			await analyzeResult(resultId);
			showSuccess("Genotyper run queued — the panel refreshes as it progresses");
			refetch();
			onRerun?.();
		} catch (err) {
			const status = axios.isAxiosError(err) ? err.response?.status : undefined;
			showError(status === 409 ? "A genotyper run is already in progress for this bundle" : "The genotyper run could not be queued");
		} finally {
			setRerunning(false);
		}
	};

	if (isLoading) {
		return <InlineLoading message="Loading genotyper run…" minHeight={120} />;
	}

	if (isError || !run) {
		const notFound = axios.isAxiosError(error) && error.response?.status === 404;
		return (
			<EmptyState
				title={notFound ? "No genotyper run yet" : "Could not load the genotyper run"}
				description={notFound ? "The run is created when the bundle upload finishes — refresh in a moment." : "labiq did not answer; try again."}
				action={{ label: "Refresh", onClick: refetch }}
				style={{ padding: "16px 0" }}
			/>
		);
	}

	const terminal = isTerminalRunStatus(run.status);
	const hasSampleErrors = run.samples.some((sample) => !!sample.error_code);

	return (
		<div data-testid="results-genotyper-panel" style={{ display: "flex", flexDirection: "column", gap: 12, padding: "4px 0" }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
				<RunHeader run={run} isPolling={isPolling} />
				<span style={{ display: "inline-flex", gap: 6 }}>
					<Button
						size="small"
						icon={<DownloadOutlined />}
						onClick={handleDownload}
						loading={downloading}
						disabled={!run.artifact_file_id}
						data-testid="results-genotyper-download"
					>
						Download results zip
					</Button>
					<Popconfirm
						title="Queue a fresh genotyper run for this bundle?"
						description="The existing results for these samples are replaced when it finishes."
						okText="Re-run"
						onConfirm={handleRerun}
						disabled={!terminal || rerunning}
					>
						<Button size="small" icon={<ReloadOutlined />} loading={rerunning} disabled={!terminal} data-testid="results-genotyper-rerun">
							Re-run
						</Button>
					</Popconfirm>
				</span>
			</div>

			{run.status === "failed" && (
				<Alert
					type="error"
					showIcon
					data-testid="results-genotyper-error"
					message={
						<span>
							Run failed{run.error_code && <code style={{ marginLeft: 6, fontSize: 11 }}>{run.error_code}</code>}
						</span>
					}
					description={run.error_code ? errorCodeHint(run.error_code) : undefined}
				/>
			)}

			{run.status === "partial" && (
				<Alert type="warning" showIcon message="The run finished with some samples incomplete — check the pipeline statuses below." />
			)}

			{run.warnings?.length > 0 && (
				<Alert
					type="warning"
					showIcon
					message={`${run.warnings.length} warning${run.warnings.length === 1 ? "" : "s"}`}
					description={
						<ul style={{ margin: 0, paddingLeft: 16, fontSize: 11 }}>
							{run.warnings.map((warning, index) => (
								<li key={`${describeWarning(warning)}-${index}`} data-testid="results-genotyper-warning">
									<code style={{ fontSize: 11 }}>{describeWarning(warning)}</code>
									{warningHint(warning.code) && (
										<Text type="secondary" style={{ fontSize: 11 }}>
											{" "}— {warningHint(warning.code)}
										</Text>
									)}
								</li>
							))}
						</ul>
					}
				/>
			)}

			{run.samples.length > 0 ? (
				<DataTable
					bare
					columns={sampleColumns(hasSampleErrors)}
					dataSource={run.samples}
					rowKey="sample_id"
					pagination={false}
					scrollX="max-content"
					data-testid="results-genotyper-samples"
				/>
			) : (
				<EmptyState title="No samples recorded yet" description="Sample rows appear once the bundle has been extracted." style={{ padding: "16px 0" }} />
			)}
		</div>
	);
};

export default GenotyperRunPanel;
